<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { asyncComputed } from '@vueuse/core'
import { useTranslation } from "i18next-vue";

import ApiConnection from "@/api";

import { SettingTypes, CfgGroups } from "@/types/enums";
import type { ChangeCallbackParams, DeviceMeta, CfgWiFi } from "@/types/config";

import { appStateStore } from "@/stores/appState";
import { configStore } from "@/stores/config";
import { addErrorBottom, addErrorFull } from "@/stores/messages";
import { systemBaseStore } from "@/stores/systemBase";

import UCInput from "@/components/ui/UCInput.vue";
import UCToggle from "@/components/ui/UCToggle.vue";
import UCRange from '@/components/ui/UCRange.vue';
import SettingsOptionButton from "@/components/settings/SettingsOptionButton.vue";
import Dialog from "@/components/elements/Dialog.vue";

const appState = appStateStore();
const config = configStore();
const systemBase = systemBaseStore();

const { t } = useTranslation();

const deviceMeta = ref<DeviceMeta | null>(null);

const bt = ref(false);
const btAddress = ref('');
const peripheralConnections = ref(1);
const hciLogs = ref(false);
const wifi = ref(false);

const wifiScanning = ref(false);
const wifiScanningInterval = ref(0);
const activeWifiBand = ref({ label: '', value: '' });
const activeWifiBands = ref<{label: string, value: string}[]>([]);

const dialogReboot = ref();
const settingsSection = ref();

const systemApi = ApiConnection.system;

config.$subscribe((mutation, state) => {
  setConfigValues();
});

const wifiScanningIntervalSetting = computed(() => {
  return {
    name: "wifi_scanning_interval",
    value: wifiScanningInterval.value,
    type: SettingTypes.SECONDS,
    group: CfgGroups.network_wifi,
    settings: {
      min: 10,
      max: 60,
      step: 1,
      showLimits: true,
    }
  }
})

const isSecondModel = computed(() => {
  return deviceMeta.value?.model?.toLowerCase() === 'ucr2';
});

const wifiAddress = asyncComputed(async() => {
  try {
    const status = await systemBase.getWifiStatus();
    return status?.address;
  } catch (error: any) {
    return(error.response.data.message);
  }
})

function setConfigValues() {
  bt.value = config.$state.config?.network?.bt_enabled || false;
  btAddress.value = config.$state.config?.network?.bt?.address || '';
  peripheralConnections.value = config.$state.config?.bt?.peripheral_connections || 1;
  hciLogs.value = config.$state.config?.bt?.enable_hci_log || false;
  wifi.value = config.$state.config?.network?.wifi_enabled || false;

  if (config.$state.wifiSettings) {
    setWifiScanning(config.$state.wifiSettings);

    if (!isSecondModel.value) {
      setWifiBand(config.$state.wifiSettings);
    }
  }
}

async function setWifiScanning(data?: CfgWiFi) {
  const settings = data ? data : await config.getWiFiSettings();
  const intervalSec = settings.scan_interval_sec;

  if (typeof intervalSec == 'number') {
    wifiScanning.value = intervalSec > 0;
    wifiScanningInterval.value = intervalSec;
  }
}

async function setWifiBand(data?: CfgWiFi) {
  const settings = data ? data : await config.getWiFiSettings();

  if (settings.band) {
    activeWifiBand.value = { label: settings.band == 'auto' ? t('settings.wifi_bluetooth.wifi_band.option.auto') : getWifiBandLabel(settings.band), value: settings.band };
  }

  if (settings.bands && settings.bands.length > 0) {
    let bands = [{ label: t('settings.wifi_bluetooth.wifi_band.option.auto'), value: 'auto' }];
    bands = bands.concat(settings.bands.map(b => ({ label: getWifiBandLabel(b), value: b })));
    activeWifiBands.value = bands;
  }
}

async function wifiChanged() {
  const params: ChangeCallbackParams = {
    group: CfgGroups.network,
    name: "wifi_enabled",
    value: wifi.value
  }
  onItemChange(params);
}

async function wifiBandChanged(item: any) {
  const params: ChangeCallbackParams = {
    group: CfgGroups.network_wifi,
    name: "band",
    value: item.value
  }
  onItemChange(params);
}

function peripheralConnectionsChange() {
  if (peripheralConnections.value > 5) {
    peripheralConnections.value = 5;
  }

  if (peripheralConnections.value < 1) {
    peripheralConnections.value = 1;
  }

  onItemChangeByList(
    [{
      name: "peripheral_connections",
      value: peripheralConnections.value,
    }]
  )
  .then(() => {
    dialogReboot.value.open();
  });
}

function hciLogsChanged() {
  onItemChangeByList(
    [{
      name: "enable_hci_log",
      value: hciLogs.value,
    }]
  )
  .then(() => {
    dialogReboot.value.open();
  });
}

async function btChanged() {
  const params: ChangeCallbackParams = {
    group: CfgGroups.network,
    name: "bt_enabled",
    value: bt.value
  }
  onItemChange(params);
}

async function wifiScanningChanged(val?: number) {
  let newValue = 0;

  if (val) {
    newValue = val;
  } else {
    newValue = wifiScanning.value ? 15 : 0;
    wifiScanningInterval.value = newValue;
  }

  const params: ChangeCallbackParams = {
    group: CfgGroups.network_wifi,
    name: "scan_interval_sec",
    value: newValue
  }

  onItemChange(params);
}

function wifiScanningIntervalChanged(params: ChangeCallbackParams) {
  if (typeof params.value == 'number') {
    wifiScanningChanged(params.value)
  }
}

async function onItemChange(params: ChangeCallbackParams) {
  try {
    await config.update(params.group as string, params.name as string, params.value);
  } catch (e: any) {
    addErrorBottom(e, null, settingsSection.value);
  }
}

async function onItemChangeByList(list: ChangeCallbackParams[]): Promise<void> {
  return new Promise(async (resolve, reject) => {
    try {
      await config.updateByList(CfgGroups.bt as string, list);
      resolve();
    } catch (e: any) {
      addErrorBottom(e, null, settingsSection.value);
      reject(e);
    }
  });
}

function getWifiBandLabel(text: string): string {
  const bandMap: Record<string, string> = {
    a: '5 Ghz',
    b: '2.4 Ghz',
  };
  return bandMap[text] || '';
}

async function reboot() {
  try {
    await systemApi.restartRemote();
    appState.setRestarting(true);
  } catch (e: any) {
    addErrorFull(e);
  }
}

onMounted(async () => {
  setConfigValues();
  setWifiScanning();

  try {
    deviceMeta.value = await config.getDeviceMeta();
	} catch (e: any) {
		console.error(e);
	}

  if (!isSecondModel.value) {
    setWifiBand();
  }
});

</script>
<template>
  <div class="page-settings-section page-settings-section--wifi-bluetooth" ref="settingsSection">
    <h1 class="page-settings-section__title">
      {{ $t('settings.wifi_bluetooth.name') }}
    </h1>
    <div class="page-settings-section__main">
      <UCToggle
        v-model="bt"
        @change="btChanged"
        :label="$t('settings.wifi_bluetooth.bluetooth.label', 'Bluetooth')"
        :full-w="true"
        :settings="true"
        :description="btAddress"
      />
      <div v-if="config.config && config.config.bt && config.config.bt.version" class="page-settings-section__param">
        <span class="page-settings-section__param__label">{{ $t('settings.wifi_bluetooth.bluetooth.current_version') }}</span>
        <span class="page-settings-section__param__value">{{ config.config.bt.version }}</span>
      </div>
      <div v-if="config.config && config.config.bt && config.config.bt.advertisement_name" class="page-settings-section__param page-settings-section__param">
        <span class="page-settings-section__param__label">{{ $t('settings.wifi_bluetooth.bluetooth.advertisement_name') }}</span>
        <span class="page-settings-section__param__value">{{ config.config.bt.advertisement_name }}</span>
      </div>
      <div class="page-settings-section__divider"></div>
      
      <SettingsOptionButton
        :label="$t('settings.wifi_bluetooth.bluetooth.peripheral_connections.label')"
        :description="$t('settings.wifi_bluetooth.bluetooth.peripheral_connections.description')"
        :type="'peripheral-connections'"
      >
        <template #customFields>
          <UCInput
            v-model="peripheralConnections"
            @change="peripheralConnectionsChange"
            :number-min="1"
            :number-max="5"
            :type="'number'"
          />
        </template>
      </SettingsOptionButton>
      <div class="page-settings-section__divider"></div>
      <UCToggle
        v-model="hciLogs"
        @change="hciLogsChanged"
        :label="$t('settings.wifi_bluetooth.bluetooth.hci_logs.label')"
        :full-w="true"
        :settings="true"
        :description="$t('settings.wifi_bluetooth.bluetooth.hci_logs.description')"
      />
      <UCToggle
        v-model="wifi"
        @change="wifiChanged"
        :label="$t('settings.wifi_bluetooth.wifi')"
        :full-w="true"
        :settings="true"
        :description="wifiAddress"
      />
      <div class="page-settings-section__main__options page-settings-section__main__options-wifi-scanning">
        <UCToggle
          v-model="wifiScanning"
          @change="wifiScanningChanged()"
          :label="$t('settings.wifi_bluetooth.wifi_scanning.label')"
          :full-w="true"
          :settings="true"
          :description="$t('settings.wifi_bluetooth.wifi_scanning.description')"
        />
        
        <UCRange
          v-if="wifiScanning"
          v-model="wifiScanningInterval"
          :change-callback="wifiScanningIntervalChanged"
          :key="'wifi-scanning-interval-value'"
          :definition="wifiScanningIntervalSetting"
          :name="wifiScanningIntervalSetting.name"
          :value="wifiScanningInterval!"
          :settings="true"
        />
      </div>

      <div v-if="!isSecondModel" class="page-settings-section__divider"></div>
      <SettingsOptionButton
        v-if="!isSecondModel"
        :select="true"
        :selectUpdate="wifiBandChanged"
        :activeOptionItem="activeWifiBand"
        :selectItems="activeWifiBands"
        :label="$t('settings.wifi_bluetooth.wifi_band.label')"
        :selectSmallScreenPosition="'right'"
        :selectDynamicPosition="true"
      />
    </div>
    <Dialog
      ref="dialogReboot"
      @submit="reboot"
      :title="$t('reboot.title')"
      :text="$t('reboot.description')"
      :submit-text="$t('ui.reboot')"
      :cancel-text="$t('ui.cancel')"
    />
  </div>
</template>